import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { addDays, format, formatDistanceToNowStrict } from 'date-fns'
import { formatInTimeZone, toDate } from 'date-fns-tz'
import { enUS } from 'date-fns/locale'

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export const APP_TIMEZONE = 'Europe/Berlin'

export function formatDate(value: Date | string | null | undefined, pattern = 'dd.MM.yyyy'): string {
    if (!value) return ''
    return formatInTimeZone(new Date(value), APP_TIMEZONE, pattern, { locale: enUS })
}

export function formatDateTime(value: Date | string | null | undefined): string {
    if (!value) return ''
    return formatInTimeZone(new Date(value), APP_TIMEZONE, 'dd.MM.yyyy HH:mm', { locale: enUS })
}

export function formatRelative(value: Date | string | null | undefined): string {
    if (!value) return ''
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true, locale: enUS })
}

export function formatIsoDate(value: Date): string {
    return format(value, 'yyyy-MM-dd')
}

/** HTML `type="date"`: Calendartag in Europe/Berlin. */
export function toDateInputValue(d: Date | string | null | undefined): string {
    if (!d) return ''
    return formatInTimeZone(new Date(d), APP_TIMEZONE, 'yyyy-MM-dd', { locale: enUS })
}

/** Standard-due date „nächster Schritt“ bei Neuanlage: heute + 3 calendar days (Berlin). */
export function defaultNextActionDueIsoDate(): string {
    return formatInTimeZone(addDays(new Date(), 3), APP_TIMEZONE, 'yyyy-MM-dd', { locale: enUS })
}

/** Standard-Event date bei Neuanlage in event phases: heute + 7 calendar days (Berlin). */
export function defaultPipelineEventDueIsoDate(): string {
    return berlinDateInputPlusDays(7)
}

/** Calendartag relativ zu heute als `yyyy-MM-dd` in `APP_TIMEZONE` (für `type="date"`). */
export function berlinDateInputPlusDays(days: number): string {
    return formatInTimeZone(addDays(new Date(), days), APP_TIMEZONE, 'yyyy-MM-dd', { locale: enUS })
}

/**
 * Wert für `datetime-local`: Calendartag in Berlin + feste Uhrzeit 09:00 (Schnellwahl-UX).
 */
export function berlinDatetimeLocalNineAmPlusDays(days: number): string {
    return `${berlinDateInputPlusDays(days)}T09:00`
}

/**
 * `yyyy-MM-dd` → UTC-Instant am **Ende** dieses Calendartags in `APP_TIMEZONE`
 * (sinnvoll für „fällig an diesem Tag“ in Abfragen).
 */
export function nextActionDueIsoToStorage(isoDate: string): Date {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim())
    if (!m) throw new RangeError('INVALID_ISO_DATE')
    const y = m[1]
    const mo = m[2]
    const d = m[3]
    return toDate(`${y}-${mo}-${d}T23:59:59.999`, { timeZone: APP_TIMEZONE })
}

/** Ende des heutigen Calendartags in `APP_TIMEZONE` (für Dashboard / Filter). */
export function endOfTodayInAppTimeZone(): Date {
    const ymd = formatInTimeZone(new Date(), APP_TIMEZONE, 'yyyy-MM-dd', { locale: enUS })
    return nextActionDueIsoToStorage(ymd)
}

/** Beginn des heutigen Calendartags in `APP_TIMEZONE`. */
export function startOfTodayInAppTimeZone(): Date {
    const ymd = formatInTimeZone(new Date(), APP_TIMEZONE, 'yyyy-MM-dd', { locale: enUS })
    return toDate(`${ymd}T00:00:00.000`, { timeZone: APP_TIMEZONE })
}

/** Wert für HTML `datetime-local` in der App-Timezone (Europe/Berlin). */
export function toDatetimeLocalValue(d: Date | string | null | undefined): string {
    if (!d) return ''
    return formatInTimeZone(new Date(d), APP_TIMEZONE, "yyyy-MM-dd'T'HH:mm", { locale: enUS })
}

export function fullName(
    person: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined
): string {
    if (!person) return ''
    const parts = [person.firstName ?? '', person.lastName ?? ''].filter(Boolean)
    if (parts.length > 0) return parts.join(' ')
    return person.email ?? ''
}

export function initials(
    person: { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined
): string {
    if (!person) return '?'
    const first = person.firstName?.[0] ?? ''
    const last = person.lastName?.[0] ?? ''
    const letters = `${first}${last}`.trim().toUpperCase()
    if (letters) return letters
    return (person.email ?? '?').slice(0, 2).toUpperCase()
}

export function truncate(value: string | null | undefined, max: number): string {
    if (!value) return ''
    return value.length > max ? `${value.slice(0, max - 1)}\u2026` : value
}

const MS_DAY = 86_400_000
const MS_HOUR = 3_600_000

/**
 * Text für „noch X Tage“ bzw. unter einem Tag „noch X Stunden“, oder Overduekeit.
 */
export function formatRemainingToDeadline(
    due: Date | string,
    nowMs: number = Date.now()
): { text: string; overdue: boolean } {
    const dueMs = new Date(due).getTime()
    const diff = dueMs - nowMs
    if (diff <= 0) {
        const over = -diff
        const days = Math.floor(over / MS_DAY)
        if (days >= 1) {
            return { text: `Overdue by ${days} ${days === 1 ? 'day' : 'days'}`, overdue: true }
        }
        const hours = Math.max(1, Math.ceil(over / MS_HOUR))
        return { text: `Overdue by ${hours} ${hours === 1 ? 'hr' : 'hrs'}`, overdue: true }
    }
    const days = Math.floor(diff / MS_DAY)
    if (days >= 1) {
        return { text: `${days} ${days === 1 ? 'day' : 'days'} left`, overdue: false }
    }
    const hours = Math.max(1, Math.ceil(diff / MS_HOUR))
    return { text: `${hours} ${hours === 1 ? 'hr' : 'hrs'} left`, overdue: false }
}

/**
 * Verstrichener Anteil zwischen Fensterstart und due date (0–100), null wenn ungültig.
 */
export function deadlineProgressPercent(nowMs: number, startMs: number | null, dueMs: number | null): number | null {
    if (startMs == null || dueMs == null) return null
    const span = dueMs - startMs
    if (span <= 0) return null
    const raw = (nowMs - startMs) / span
    return Math.min(100, Math.max(0, raw * 100))
}
